/**
 * Cylindo AR Link Component
 * For simple/finished products without 3D viewer
 * Displays a "See it in your space" link that opens AR experience via QR code
 *
 * Based on Cylindo Content API ARQR:
 * https://pdp.cylindo.com/API/content-api#arqr
 */

import React, {useId, useMemo} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';

import {
  Box, Button, Heading, Image, Modal, ModalBody, ModalContent,
  ModalOverlay, Text, useDisclosure, useMediaQuery, useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const CYLINDO_AR_MODAL_BG = 'static/img/cylindo-ar-modal-bg.png';
const ICON_BACK_TO_PREVIEWER = 'static/img/icon-back-to-previewer.svg';
const MOBILE_DEVICES_SVG = 'static/img/mobile-devices.svg';
const QR_CODE_FRAME = 'static/img/qr-code-frame.png';

const CylindoARLink = ({
  arqrUrl,
  productName,
  buttonText,
  buttonProps,
  compact = false,
  onMobileClick,
}) => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  const intl = useIntl();
  const resolvedProductName = useMemo(() => {
    if (productName && String(productName).trim()) {
      return productName;
    }
    return intl.formatMessage({
      defaultMessage: 'this product',
      id: 'cylindo_ar.product.fallback_name',
    });
  }, [intl, productName]);
  const titleId = useId();
  const [isDesktop] = useMediaQuery('(min-width: 1280px)');
  const styles = useMultiStyleConfig('ImageGallery', {});
  const arModalBgUrl = useMemo(() => getAssetUrl(CYLINDO_AR_MODAL_BG), []);
  const backIconUrl = useMemo(() => getAssetUrl(ICON_BACK_TO_PREVIEWER), []);
  const mobileDevicesUrl = useMemo(() => getAssetUrl(MOBILE_DEVICES_SVG), []);
  const qrFrameUrl = useMemo(() => getAssetUrl(QR_CODE_FRAME), []);
  const cylindoArModalContentProps = useMemo(() => {
    const raw = styles?.cylindoArModalContent ?? {};
    const {sx: themeSx, ...rest} = raw;
    return {
      ...rest,
      sx: {
        ...themeSx,
        // Chakra Modal sets `background` via --modal-bg; longhands keep the photo visible
        '--modal-bg': 'unset',
        backgroundColor: 'transparent',
        backgroundImage: `url(${arModalBgUrl})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      },
    };
  }, [arModalBgUrl, styles?.cylindoArModalContent]);
  const cylindoArQrBoxProps = useMemo(() => {
    const raw = styles?.cylindoArQrContainer ?? {};
    const {sx: themeSx, ...rest} = raw;
    return {
      ...rest,
      sx: {
        ...themeSx,
        backgroundColor: 'transparent',
        backgroundImage: `url(${qrFrameUrl})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      },
    };
  }, [qrFrameUrl, styles?.cylindoArQrContainer]);
  const cylindoArBackIconProps = useMemo(() => {
    const raw = styles?.cylindoArModalBackButtonIcon ?? {};
    const {sx: themeSx, ...rest} = raw;
    return {
      ...rest,
      sx: {
        ...themeSx,
        backgroundColor: 'transparent',
        backgroundImage: `url(${backIconUrl})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      },
    };
  }, [backIconUrl, styles?.cylindoArModalBackButtonIcon]);
  const {leftIcon: customLeftIcon, sx: customButtonSx, ...restButtonProps} = buttonProps || {};
  const leftIcon = compact ? undefined : (
    customLeftIcon || (
      <Box as="span" fontSize="xl">
        📱
      </Box>
    )
  );
  const compactSx = compact ?
    (customButtonSx ?? styles?.cylindoArButtonCompact) :
    customButtonSx;
  const shouldUseAnchor = !isDesktop && !onMobileClick;

  if (!arqrUrl) return null;

  const defaultButtonText = buttonText || intl.formatMessage({
    defaultMessage: 'See it in your space',
    id: 'cylindo_ar.button.see_in_space',
  });

  return (
    <>
      {/* AR Link Button */}
      <Button
        colorScheme={compact ? undefined : 'blue'}
        mt={compact ? 0 : 4}
        size={compact ? 'sm' : 'lg'}
        variant="outline"
        width={compact ? 'auto' : 'full'}
        {...restButtonProps}
        as={shouldUseAnchor ? 'a' : undefined}
        href={shouldUseAnchor ? arqrUrl : undefined}
        leftIcon={leftIcon}
        sx={compactSx}
        onClick={isDesktop ? onOpen : onMobileClick}
      >
        {defaultButtonText}
      </Button>

      {/* QR Code Modal — full viewport below header, frosted overlay, centered stack (livesite-style) */}
      {isDesktop && (
        <Modal
          blockScrollOnMount
          closeOnOverlayClick
          isCentered={false}
          isOpen={isOpen}
          scrollBehavior="inside"
          size="full"
          onClose={onClose}
        >
          <ModalOverlay {...(styles?.cylindoArModalOverlay ?? {})} />
          <ModalContent
            aria-labelledby={titleId}
            margin="0"
            {...cylindoArModalContentProps}
          >
            <Button
              aria-label={intl.formatMessage({
                defaultMessage: 'Back to 360 view',
                id: 'cylindo_ar.modal.back_to_360.aria',
              })}
              type="button"
              variant="unstyled"
              {...(styles?.cylindoArModalBackButton ?? {})}
              onClick={onClose}
            >
              <Box aria-hidden as="span" {...cylindoArBackIconProps} />
              <Box as="span" {...(styles?.cylindoArModalBackButtonLabel ?? {})}>
                <FormattedMessage
                  defaultMessage="Back to 360 view"
                  id="cylindo_ar.modal.back_to_360"
                />
              </Box>
            </Button>

            <ModalBody {...(styles?.cylindoArModalBody ?? {})}>
              <VStack {...(styles?.cylindoArModalStack ?? {})}>
                <Heading
                  as="h2"
                  id={titleId}
                  whiteSpace="pre-line"
                  {...(styles?.cylindoArModalTitle ?? {})}
                >
                  <FormattedMessage
                    defaultMessage={'View this product\nin your space'}
                    id="cylindo_ar.modal.title"
                  />
                </Heading>

                <Text {...(styles?.cylindoArModalDescription ?? {})}>
                  <FormattedMessage
                    defaultMessage="Scan this QR code with your mobile device to see {productName} in your space"
                    id="cylindo_ar.modal.description"
                    values={{productName: resolvedProductName}}
                  />
                </Text>

                <Box aria-hidden {...(styles?.cylindoArModalDeviceIcons ?? {})}>
                  <Image
                    alt=""
                    draggable={false}
                    loading="eager"
                    src={mobileDevicesUrl}
                  />
                </Box>

                <Box {...cylindoArQrBoxProps}>
                  <Image
                    {...(styles?.cylindoArQrImage ?? {})}
                    alt={intl.formatMessage({
                      defaultMessage: 'QR code to view {productName} in AR',
                      id: 'cylindo_ar.qr_code.alt',
                    }, {productName: resolvedProductName})}
                    src={arqrUrl}
                  />
                </Box>

                <Text {...(styles?.cylindoArModalFooter ?? {})}>
                  <FormattedMessage
                    defaultMessage="Works on iOS and Android devices with AR support"
                    id="cylindo_ar.modal.footer"
                  />
                </Text>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

CylindoARLink.propTypes = {
  arqrUrl: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  buttonProps: PropTypes.object,
  compact: PropTypes.bool,
  onMobileClick: PropTypes.func,
  productName: PropTypes.string,
};

export default CylindoARLink;
